import {
  ALL_HOTEL_TYPES,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_EXPERIENCE,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_V2,
} from 'constants/offer'
import config from 'constants/config'
import request from 'api/requestUtils'

import { templates } from '@luxuryescapes/lib-uri-templates'
import { isHotelSpecificRequest } from 'lib/search/searchUtils'
import { serialiseOccupancy } from 'lib/search/occupancy'
import {
  getDestinationIds,
  getLandmarkIds,
  getPlaceIdsForSearchApi,
} from 'lib/search/filterIdUtils'
import { hotelOfferMap } from './mappers/hotelOfferMap'
import { tourV2OfferMap, tourV2OfferSummaryMap } from './mappers/TourV2/tourV2OfferMapper'
import { traderInformationMap } from './mappers/traderInformation'
import { bedBankOfferMap } from './mappers/bedbankOfferMap'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import qs from 'qs'
import type { PublicOfferV2, paths as publicOfferPaths } from '@luxuryescapes/contract-public-offer'
import type { paths as searchPaths, InitialRequest as SearchInitialRequest, RankingPreferences } from '@luxuryescapes/contract-search'
import wait from 'lib/promise/wait'
import { EmptyArray, arrayToObject, nonNullable, split } from 'lib/array/arrayUtils'
import { isTourV2OfferSummary } from 'lib/offer/offerTypes'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { getCruiseSearchList, getCruiseOfferByIdType } from './cruises'
import { detectOfferTypeFromId } from 'lib/offer/offerUtils'
import { getSearchLight, getTourSearchListByFacet } from './search'
import type { OfferListResult } from './offerList.types'
import { isValidOccupancy } from 'lib/offer/occupancyUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { tourOfferMap } from './mappers/tourOfferMap'
import { villaOfferMap } from './mappers/villaOfferMap'
import { bundleOfferMap } from './mappers/bundleOfferMap'
import { emitSocketMessage } from 'lib/search/socketEmitter'
import type { definitions as accommodationDefs } from '@luxuryescapes/contract-svc-accommodation'
import type { paths as flightPaths } from '@luxuryescapes/contract-svc-flights'
import { getExperienceById, getExperienceSearchList } from './experiences'
import { getExperienceSearchStreamList } from './experienceSearchStream'
import { safeNumberConversion } from '../lib/string/stringUtils'

export type { OfferFilterResult, OfferListResult, TourSearchResultResponse } from './offerList.types'

export type HotelSearchResultResponse = searchPaths['/api/search/hotel/v1/list']['get']['responses']['200']['content']['application/json']

export type PackageRecommendationsResponse = publicOfferPaths['/api/v2/public-offers/{id}/package-recommendations']['get']['responses']['200']['content']['application/json']

export interface PresignedUrlRequest {
  filename: string
  expires_in?: number
  bucket_name?: string
}

export interface PresignedUrlResponse {
  success: boolean
  presigned_url: string
  filename: string
}

export type SearchOffer = {
  kind: string
  id: string
  packages?: Array<string>
  distance?: number
  available?: boolean
  suggestedTravelDates?: {
    /** Format: ISO8601 */
    checkIn: string
    /** Format: ISO8601 */
    checkOut: string
    price?: number
    memberPrice: number
    value?: number
    currency?: string
    packageId?: string
  }
}

// Accumulators are keyed by the bypassRegionFilter flag: the flag applies to the whole batched
// request, so bypass and non-bypass calls landing in the same 32ms window must not share a batch —
// otherwise whichever call created the batch decides the flag for every offer in it.
interface OfferFetchBatch {
  accumulator: Array<string>
  promise: Promise<{ [offerId: string]: App.AnyOffer }> | undefined
}
const offerFetchBatches: Record<'default' | 'bypassRegion', OfferFetchBatch> = {
  default: { accumulator: [], promise: undefined },
  bypassRegion: { accumulator: [], promise: undefined },
}

/**
 * This function will get and return a single offer by ID
 * However, it won't always do this straight away or in a separate request
 * The function will 'accumulate' all offer requests over a period of time and then fetch them
 * all in one go - returning them individually to the appropriate function call
 * @param id The ID of the offer to fetch
 * @param region Regioncode for the fetch
 * @param flightOrigin Flight origin for the fetch
 * @returns A promise with the offer requested
 */
// Bridges callers that still pass a bare v1 `channelMarkup` into the mode-tagged `ChannelMarkupSource`
// the mappers now expect; an existing `ChannelMarkupSource` passes through unchanged.
type MarkupSourceInput = ChannelMarkupSource | App.ChannelMarkup
function toChannelMarkupSource(input?: MarkupSourceInput): ChannelMarkupSource | undefined {
  if (!input) return undefined
  return 'mode' in input ? input : { mode: 'v1', channelMarkup: input }
}

export function getAccumulatedOfferById(id: string, region: string, flightOrigin?: string, currentCurrency?: string, isSpoofed?: boolean, markupSource?: ChannelMarkupSource, offerDeepLink?: string, bypassRegionFilter?: boolean) {
  if (detectOfferTypeFromId(id) === 'cruise') {
    // cruises don't have a multi fetch version to accumulate
    return getOfferById(id, { region, flightOrigin, currentCurrency, isSpoofed })
  }

  // TODO remove this when we implement fetch for multiple offers:
  // https://aussiecommerce.atlassian.net/browse/EXP-2369
  if (detectOfferTypeFromId(id) === 'experience') {
    // experiences don't have a multi fetch version to accumulate yet
    return getOfferById(id, { region, flightOrigin, currentCurrency, isExperience: true, markupSource })
  }

  const batch = offerFetchBatches[bypassRegionFilter ? 'bypassRegion' : 'default']
  batch.accumulator.push(id)
  if (!batch.promise) {
    // give it 32ms to accumulate offer calls - that's around two frames at 60 fps worth of time
    batch.promise = wait(32).then(async() => {
      const offersToFetch = batch.accumulator
      // reset our state in prep for next set of calls
      batch.accumulator = []
      batch.promise = undefined

      const offers = await getOffersById(offersToFetch, region, { flightOrigin, isSpoofed, bypassRegionFilter }, markupSource, offerDeepLink)

      const offersById = arrayToObject(offers, offer => isTourV2OfferSummary(offer) ? offer.summaryId : offer.id)
      return offersById
    })
  }

  return batch.promise.then((offers) => {
    const offer = offers[id]
    if (!offer) {
      return Promise.reject({ status: 404, message: `Offer not found ${id}` })
    }
    return offer
  })
}

interface GetOfferOptions {
  region: string
  flightOrigin?: string
  allPackages?: boolean
  bypassRegionFilter?: boolean
  preview?: string
  privateRequestKey?: string
  currentCurrency?: string
  isCruise?: boolean
  isSpoofed?: boolean
  markupSource?: ChannelMarkupSource
  channelMarkup?: App.ChannelMarkup
  isExperience?: boolean
  source?: string
  offerDeepLink?: string
}

export function getOfferById(
  id: string,
  { region, flightOrigin, allPackages = false, bypassRegionFilter = false, preview, privateRequestKey, currentCurrency, isCruise, isSpoofed, isExperience, markupSource: markupSourceInput, channelMarkup, source, offerDeepLink }: GetOfferOptions,
): Promise<App.HotelOffer | App.TourOffer | App.BedbankOffer | App.CruiseOffer | App.Tours.TourV2Offer | App.BundleOffer | App.VillaOffer | App.ExperienceOffer> {
  const markupSource = markupSourceInput ?? toChannelMarkupSource(channelMarkup)
  // TODO: Remove this once we have support for cruise in the public offer - https://aussiecommerce.atlassian.net/browse/CRUZ-2932
  if (isCruise || detectOfferTypeFromId(id) === 'cruise') {
    const allowInvalidOffer = !!isSpoofed
    return getCruiseOfferByIdType(id, region, currentCurrency, allowInvalidOffer)
  }

  if (isExperience || detectOfferTypeFromId(id) === 'experience') {
    return getExperienceById(id, { currentCurrency, currentRegionCode: region })
  }

  const uri = templates.offer.publicOffer.expand({ id })

  const params = qs.stringify({
    flightOrigin,
    region,
    allPackages,
    bypassRegionFilter,
    preview,
    privateRequestKey: privateRequestKey ?? undefined,
    source,
    offerDeepLink,
  })

  const includeCredentials = isSpoofed || !!offerDeepLink

  return request.get<App.ApiResponse<PublicOfferV2.Offer>>(`${uri}?${params}`, includeCredentials ? { credentials: 'include' } : {})
    .then(async response => {
      if (response.result.type === OFFER_TYPE_BED_BANK) {
        return bedBankOfferMap(response.result, source, markupSource)
      } else if (response.result.type === OFFER_TYPE_TOUR_V2) {
        return tourV2OfferMap(response.result)
      } else if (response.result.type === 'bundle_and_save') {
        return bundleOfferMap(response.result, region, markupSource)
      } else if (response.result.type === 'tour') {
        return tourOfferMap(response.result)
      } else if (response.result.type === 'rental') {
        return villaOfferMap(response.result, region, markupSource)
      } else {
        return hotelOfferMap(response.result, region, markupSource)
      }
    })
}

interface GetOffersByIdParams {
  checkIn?: string
  checkOut?: string
  occupancies?: Array<App.Occupants>
  flightOrigin?: string
  preview?: string
  currentCurrency?: string
  isSpoofed?: boolean
  enablePropertyCollect?: boolean
  bypassRegionFilter?: boolean
}

export interface BuildPublicOffersUrlParams {
  checkIn?: string
  checkOut?: string
  region: string
  occupancies?: Array<App.Occupants>
  flightOrigin?: string
  preview?: string
  offerDeepLink?: string
  enablePropertyCollect?: boolean
  bypassRegionFilter?: boolean
}

type PublicOffersResponse = App.ApiResponse<Array<PublicOfferV2.Offer>>

interface MetaSearchWarmupWindow extends Window {
  __META_SEARCH_PUBLIC_OFFERS_WARMUP__?: Record<string, Promise<PublicOffersResponse | undefined>>
}

/**
 * Build the canonical query string for `/api/v2/public-offers?offerIds=...`.
 *
 * MUST stay byte-identical to the query produced by `getOffersById` so that
 * the head warm-up script and runtime fetch path resolve to the same URL.
 *
 * Note: `brandLoader` (a request preprocessor) appends `brand=<brand>` to the
 * URL at fetch time.
 */
export function buildPublicOffersUrl(
  offerIds: Array<string>,
  params: BuildPublicOffersUrlParams,
): string {
  const uri = templates.offer.publicOffers.expand({})
  const query = qs.stringify({
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    region: params.region,
    occupancy: params.occupancies ? serialiseOccupancy(params.occupancies) : undefined,
    flightOrigin: params.flightOrigin,
    preview: params.preview,
    offerDeepLink: params.offerDeepLink,
    enablePropertyCollect: params.enablePropertyCollect,
    bypassRegionFilter: params.bypassRegionFilter || undefined,
    offerIds: offerIds.join(','),
  })
  return `${uri}?${query}`
}

/**
 * Builds the absolute URL for the public-offers endpoint and mirrors the
 * `brandLoader` preprocessor by appending `brand=<brand>`.
 *
 * This is shared by the SSR head warm-up script and runtime offer fetch path
 * so both sides key into the same in-memory warm-up map.
 */
export function buildPublicOffersAbsoluteUrl(path: string): string {
  const separator = path.includes('?') ? '&' : '?'
  const pathWithBrand = `${path}${separator}brand=${encodeURIComponent(config.BRAND)}`
  return `${config.SCHEME}://${config.API_HOST}${pathWithBrand}`
}

function getMetaSearchWarmupResponse(url: string): Promise<PublicOffersResponse | undefined> | undefined {
  if (typeof window === 'undefined') return undefined
  const warmupWindow = window as MetaSearchWarmupWindow
  return warmupWindow.__META_SEARCH_PUBLIC_OFFERS_WARMUP__?.[url]
}

/**
 * Maps a server offer to an App offer type based on the offer type
 */
export function mapServerOfferToAppOffer(
  serverOffer: PublicOfferV2.Offer,
  regionCode: string,
  markupSource?: ChannelMarkupSource,
): App.Offer | App.BedbankOffer | App.Tours.TourV2OfferSummary | undefined {
  try {
    if (serverOffer.type === OFFER_TYPE_BED_BANK) {
      return bedBankOfferMap(serverOffer, undefined, markupSource)
    } else if (serverOffer.type === OFFER_TYPE_TOUR_V2) {
      return tourV2OfferSummaryMap(serverOffer)
    } else if (serverOffer.type === 'bundle_and_save') {
      return bundleOfferMap(serverOffer, regionCode, markupSource)
    } else if (serverOffer.type === 'tour') {
      return tourOfferMap(serverOffer)
    } else if (serverOffer.type === 'rental') {
      return villaOfferMap(serverOffer, regionCode, markupSource)
    } else {
      return hotelOfferMap(serverOffer, regionCode, markupSource)
    }
  } catch {
    return undefined
  }
}

const CRUISE_EXPERIENCE_OFFER_FETCH_CONCURRENCY = 5

export async function getOffersById(
  offerIds: Array<string>,
  regionCode: string,
  params: GetOffersByIdParams = {},
  markupSourceInput?: MarkupSourceInput,
  offerDeepLink?: string,
): Promise<Array<App.CruiseOffer | App.Offer | App.BedbankOffer | App.Tours.TourV2OfferSummary | App.ExperienceOffer>> {
  const markupSource = toChannelMarkupSource(markupSourceInput)
  const isCruise = detectOfferTypeFromId(offerIds[0]) === 'cruise'
  const isExperience = detectOfferTypeFromId(offerIds[0]) === 'experience'

  if (isCruise || isExperience) {
    // getOfferById has no batch endpoint for cruise/experience, so each id is its own request.
    // Fetch in small concurrent batches instead of firing every request at once.
    const offerIdBatches = split(offerIds, CRUISE_EXPERIENCE_OFFER_FETCH_CONCURRENCY)
    const offers: Array<App.CruiseOffer> = []
    for (const batch of offerIdBatches) {
      const batchOffers = await Promise.all(batch.map(async offerId => {
        return getOfferById(offerId, { region: regionCode, currentCurrency: params.currentCurrency, isCruise, isExperience, markupSource }) })) as Array<App.CruiseOffer>
      offers.push(...batchOffers)
    }
    return offers
  }

  const offerIdsByChunks = split(offerIds, 25)
  const offers = (await Promise.all(offerIdsByChunks.map(async(offerIdsChunk: Array<string>) => {
    const url = buildPublicOffersUrl(offerIdsChunk, {
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      region: regionCode,
      occupancies: params.occupancies,
      flightOrigin: params.flightOrigin,
      preview: params.preview,
      offerDeepLink,
      enablePropertyCollect: params.enablePropertyCollect,
      bypassRegionFilter: params.bypassRegionFilter,
    })

    const warmupUrl = buildPublicOffersAbsoluteUrl(url)
    const warmupRequest = getMetaSearchWarmupResponse(warmupUrl)
    const warmupResponse = warmupRequest ? await warmupRequest.catch(() => undefined) : undefined

    const response = warmupResponse ??
      await request.get<App.ApiResponse<Array<PublicOfferV2.Offer>>>(
        url,
        params.isSpoofed ? { credentials: 'include' } : {},
      )

    return response.result.map(serverOffer => mapServerOfferToAppOffer(serverOffer, regionCode, markupSource))
  }))).flat()

  return nonNullable(offers)
}

interface GetOfferListParams {
  region: string
  filters?: App.OfferListFilters
  canViewLuxPlusBenefits: boolean
  isLuxPlusEnabled: boolean
  clientCheckAvailability?: boolean
  isSpoofed?: boolean
  accessToken?: string
  isPaidSession?: boolean
  memberId?: string
  domainUserId?: string
  markupSource?: ChannelMarkupSource
  searchStreaming?: boolean
}

interface SearchRequestPayload {
  region: string
  publicRates?: boolean
  filters?: App.OfferListFilters
  canViewLuxPlusBenefits: boolean
  isLuxPlusEnabled: boolean
  clientCheckAvailability?: boolean
  noCache?: boolean
  isPaidSession?: boolean
  /** This is used for lere personalization */
  memberId?: string
  /** This is used for lere personalization */
  domainUserId?: string
  /** Logged-in user email for FnF charter pricing eligibility */
  email?: SearchInitialRequest['email']
  /** This is used for search streaming */
  searchId?: string
  /** Min number of offers required */
  minRequiredOffers?: number
  /** This is used for lere personalization */
  personalise?: boolean
  /** Control the weights of the different ranking factors */
  functionScoreWeights?: RankingPreferences
  include?: Array<string>
  typeaheadId?: string
  lang?: string
}

const supportedToursRequestTypes: Array<App.OfferListOfferType> = [
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_TOUR,
]

const supportedExperienceRequestTypes: Array<App.OfferListOfferType> = [
  OFFER_TYPE_EXPERIENCE,
]

function isTourSearchSpecificRequest(params: GetOfferListParams) {
  const filters = params.filters ?? {}

  return !!(filters.offerTypes?.every(type => supportedToursRequestTypes.includes(type))) && !filters.holidayTypes?.includes('Cruises') && params.filters?.sortBy !== 'vertical-ultralux' && !params.filters?.useUnifiedSearchEndpoint
}

function isUnifiedSpecificRequest(params: GetOfferListParams) {
  // only unified end point accepts multiple place ids at the moment
  return params.filters &&
    ((params.filters.placeIds?.length ?? 0) > 0 ||
      params.filters.sortBy === 'home' ||
      params.filters.sortBy === 'vertical-tour' ||
      params.filters.sortBy === 'vertical-hotel' ||
      params.filters.sortBy === 'carousel-hotel' ||
      params.filters.sortBy === 'vertical-ultralux' ||
      params.filters.sortBy === 'subregion-nsw' ||
      params.filters.sortBy === 'subregion-vic' ||
      params.filters.sortBy === 'vertical-cruise' ||
      params.filters.sortBy === 'triple-points' ||
      params.filters.useUnifiedSearchEndpoint
    )
}

function isExperienceSearchSpecificRequest(params: GetOfferListParams) {
  const filters = params.filters ?? {}
  return filters.offerTypes?.every(type => supportedExperienceRequestTypes.includes(type)) && filters.sortBy !== 'vertical-ultralux' && !filters.useUnifiedSearchEndpoint
}

type UnifiedSearchResponse = searchPaths['/api/search/unified/v1/list']['get']['responses']['200']['content']['application/json']

function getOfferTypesFromFilters(filters: App.OfferListFilters): Array<App.OfferListOfferType | App.ExperienceProductType> {
  if (!filters.offerTypes) return []
  return filters.offerTypes
    .map((offerType) => {
      // svc-search's offer type for experiences is the ExperienceProductType of CP,
      // that's why we need this mapping
      if (offerType === OFFER_TYPE_EXPERIENCE) {
        return config.UNIFIED_SEARCH_EXPERIENCES_TYPES?.length ? config.UNIFIED_SEARCH_EXPERIENCES_TYPES : []
      }
      return offerType
    })
    .flat()
}

export function getOfferList(params: GetOfferListParams): Promise<OfferListResult> {
  const { filters = {}, region, canViewLuxPlusBenefits, isLuxPlusEnabled, markupSource } = params

  let searchType: string | undefined = undefined

  const destinationIds = getDestinationIds(filters)
  const landmarkIds = getLandmarkIds(filters)

  if (destinationIds && destinationIds.length > 0) {
    searchType = 'destination'
  } else if (landmarkIds && landmarkIds.length > 0) {
    searchType = 'landmark'
  }

  const scheduleVisibilities = filters.scheduleVisibilities ? [...filters.scheduleVisibilities] : []

  if (!scheduleVisibilities.includes('listVisible')) {
    scheduleVisibilities.push('listVisible')
  }

  if (isLuxPlusEnabled && !scheduleVisibilities.includes('luxPlusEarlyAccessVisible')) {
    scheduleVisibilities.push('luxPlusEarlyAccessVisible')
  }

  if (canViewLuxPlusBenefits && !scheduleVisibilities.includes('luxPlusExclusiveVisible')) {
    scheduleVisibilities.push('luxPlusExclusiveVisible')
  }

  const types = getOfferTypesFromFilters(filters)

  const commonParams = {
    offerType: types.join(',') || undefined,
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
    flexibleMonths: filters.flexibleMonths || undefined,
    flexibleNights: filters.flexibleNights || undefined,
    campaigns: filters.campaigns?.join(',') || undefined,
    holidayTypes: filters.holidayTypes?.join(',') || undefined,
    holidayTypesScoped: filters.holidayTypesScoped?.join(',') || undefined,
    propertyTypes: filters.propertyTypes?.join(',') || undefined,
    locations: filters.locations?.join(',') || undefined,
    searchType,
    placeIds: getPlaceIdsForSearchApi(filters),
    distanceEq: filters.distanceEq,
    distanceGt: filters.distanceGt,
    sortBy: filters.sortBy,
    strategyApplied: filters.strategyApplied,
    region,
    occupancy: filters.rooms ? serialiseOccupancy(filters.rooms) : undefined,
    propertyId: filters.propertyId,
    searchNearby: filters.searchNearby,
    bounds: filters.bounds,
    amenities: filters.amenities?.join(',') || undefined,
    propertyFacilities: filters.propertyFacilities?.join(',') || undefined,
    roomAmenities: filters.roomAmenities?.join(',') || undefined,
    bedTypes: filters.bedTypes?.join(',') || undefined,
    limit: filters.limit || undefined,
    propertySubCategories: filters.propertySubCategories?.join(',') || undefined,
    starRatingGte: filters.starRatingGte,
    customerRatingGte: filters.customerRatingGte,
    inclusions: filters.inclusions?.join(',') || undefined,
    priceLte: filters.priceLte,
    priceGte: filters.priceGte,
    productTypes: filters.productTypes,
    bedroomsGte: filters.bedroomsGte && filters.bedroomsGte > 0 ? filters.bedroomsGte : undefined,
    bedroomsEq: filters.bedroomsEq && filters.bedroomsEq > 0 ? filters.bedroomsEq : undefined,
    bedsGte: filters.bedsGte && filters.bedsGte > 0 ? filters.bedsGte : undefined,
    bedsEq: filters.bedsEq && filters.bedsEq > 0 ? filters.bedsEq : undefined,
    bathroomsGte: filters.bathroomsGte && filters.bathroomsGte > 0 ? filters.bathroomsGte : undefined,
    bathroomsEq: filters.bathroomsEq && filters.bathroomsEq > 0 ? filters.bathroomsEq : undefined,
    scheduleVisibilities: scheduleVisibilities?.join(',') || undefined,
    luxPlusExclusive: filters.luxPlusFeatures && filters.luxPlusFeatures.length > 0 ? filters.luxPlusFeatures?.join(',') : undefined,
    includeAllBedbankWithSales: filters.includeAllBedbankWithSales || undefined,
    isLuxPlusMember: filters.isLuxPlusMember ?? (canViewLuxPlusBenefits || undefined),
    agentHubExclusiveOffersOnly: filters.agentHubExclusiveOffersOnly || undefined,
    agentHubExclusive: filters.agentHubFeatures && filters.agentHubFeatures.length > 0 ? filters.agentHubFeatures?.join(',') : undefined,
    societe: filters.loyalty?.join(',') || undefined,
    chains: filters.chains?.join(',') || undefined,
    subChains: filters.subChains?.join(',') || undefined,
  }

  if (isHotelSpecificRequest({ filters: params.filters })) {
    // Points-aware hotel search (pointsLte/pointsGte, points.asc/points.desc sort) is only
    // supported by svc-search over the search:request:initial socket message — see
    // streamRequest()'s pointsLte/pointsGte fields below. Light search has no points support,
    // so callers needing points pricing/filtering must set filters.isStream to use the socket
    // (via useOfferList) instead of hitting this HTTP path.
    return getSearchLight({
      // The light search endpoint only supports a single place id, so we use the first one in the array
      placeId: getPlaceIdsForSearchApi(filters) ?? ANYWHERE_PLACE_ID,
      region,
      limit: filters.limit,
      markupSource,
      rollout: params.filters?.rollout,
      holidayTypes: filters.holidayTypes,
      propertyTypes: filters.propertyTypes,
      offerTypes: filters.offerTypes,
      scheduleVisibilities,
      luxPlusExclusive: filters.luxPlusFeatures,
      campaigns: filters.campaigns,
      loyalty: filters.loyalty,
    })
  } else if (params.filters?.offerTypes?.includes('cruise') && !isUnifiedSpecificRequest(params)) {
    return getCruiseSearchList(params.filters, region).then(result => {
      return {
        offerIds: result,
        searchVertical: 'cruises',
      }
    })
  } else if (isTourSearchSpecificRequest(params)) {
    return getTourSearchListByFacet({ ...params.filters, isLuxPlusMember: canViewLuxPlusBenefits }, region).then(response => {
      return {
        offerIds: response.result.map(result => {
          if (typeof result === 'string') {
            return result
          }
          return result.tourId
        }),
        tourMetadata: response.result.map(result => {
          if (typeof result === 'string') {
            return {
              tourId: result,
              tourOptionIds: [],
            }
          }
          return ({
            tourId: result.tourId,
            tourOptionIds: result.tourOptionIds,
          })
        }),
        searchVertical: 'tours',
      }
    })
  } else if (isExperienceSearchSpecificRequest(params)) {
    const placeId = filters.placeIds?.[0] ?? ANYWHERE_PLACE_ID
    const experienceParams = {
      placeId,
      region,
      luxPlusFeatures: filters.luxPlusFeatures,
      campaigns: filters.campaigns,
      sortBy: filters.sortBy as Experiences.SortTypes | undefined,
    }

    if (params.searchStreaming && placeId) {
      return getExperienceSearchStreamList({
        placeId,
        region,
        luxPlusFeatures: filters.luxPlusFeatures,
        sortBy: filters.sortBy as Experiences.SortTypes | undefined,
        campaigns: filters.campaigns,
        from: filters.checkIn,
        to: filters.checkOut,
        limit: filters.limit,
      }).then(response => ({
        offerIds: response.experienceIds,
        searchVertical: 'experiences' as const,
      }))
    }

    return getExperienceSearchList({ ...experienceParams, limit: filters.limit }).then(response => ({
      offerIds: response.experienceIds,
      searchVertical: 'experiences' as const,
      experienceOffersMetadata: response.offersMetadata,
    }))
  } else {
    const unifiedOfferTypes = commonParams.offerType ??
      (filters.offerTypes === undefined ? ALL_HOTEL_TYPES.join(',') : undefined)

    if (!unifiedOfferTypes) {
      return Promise.resolve({
        offerIds: [],
        filters: {
          campaigns: [],
          holidayTypes: [],
          bedrooms: [],
          propertyTypes: [],
          amenities: [],
          propertyFacilities: [],
          roomAmenities: [],
          bedTypes: [],
          offerTypes: [],
          luxPlusFeatures: [],
          loyalty: [],
          agentHubFeatures: [],
          paymentOptions: [],
          chains: [],
          inclusions: [],
          customerRatings: [],
          total: 0,
        },
      })
    }

    const queryString = qs.stringify({
      region,
      placeIds: filters.placeIds?.join(',') ?? commonParams.placeIds ?? ANYWHERE_PLACE_ID,
      ...(filters.limit ? { limit: filters.limit } : {}),
      offerTypes: unifiedOfferTypes,
      ...(commonParams.sortBy?.length ? { sortBy: commonParams.sortBy } : {}),
      ...(commonParams.holidayTypes?.length ? { holidayTypes: commonParams.holidayTypes } : {}),
      ...(commonParams.holidayTypesScoped?.length ? { holidayTypesScoped: commonParams.holidayTypesScoped } : {}),
      ...(commonParams.locations?.length ? { locations: commonParams.locations } : {}),
      ...(commonParams.campaigns?.length ? { campaigns: commonParams.campaigns } : {}),
      ...(filters.loyalty?.length ? { societe: filters.loyalty?.join(',') } : {}),
      ...(filters.chains?.length ? { chains: filters.chains?.join(',') } : {}),
      ...(filters.subChains?.length ? { subChains: filters.subChains?.join(',') } : {}),
      isLuxPlusMember: canViewLuxPlusBenefits,
      earlyAccessOffersOnly:
        filters.luxPlusFeatures?.includes('early_access') ||
        filters.luxPlusFeatures?.includes('Early access') ||
        false,
      agentHubExclusiveOffersOnly: commonParams.agentHubExclusiveOffersOnly || false,
    })

    return request.get<UnifiedSearchResponse>(`/api/search/unified/v1/list?${queryString}`).then((response): OfferListResult => {
      return {
        offerIds: response.results.map(offer => offer.offerType == 'cruise' ? `cruise:${offer.bk}` : offer.bk),
        filters: {
          campaigns: response.filtersV2.campaigns ?? [],
          holidayTypes: response.filtersV2.holidayTypes ?? [],
          bedrooms: [],
          propertyTypes: [],
          amenities: [],
          propertyFacilities: [],
          roomAmenities: [],
          bedTypes: [],
          offerTypes: [],
          luxPlusFeatures: [],
          loyalty: [],
          agentHubFeatures: [],
          paymentOptions: [],
          chains: [],
          inclusions: [],
          customerRatings: [],
          total: response.total,
        },
      }
    })
  }
}

export function getTraderInformation(id: string, region: string) {
  const uri = templates.offer.publicOfferTraderInformation.expand({ propertyId: id, region })

  return request.get<App.ApiResponse<PublicOfferV2.TraderInformation | null>>(uri)
    .then(async data => {
      if (data.result) {
        return traderInformationMap(data.result)
      }
      return null
    })
}

interface FlightPriceParams {
  region: string
  flightOrigin: string
  flightDestination: string
  currency: string
  duration: number
  travelToDate?: string
  forceBundleId?: string
}

type FlightPriceResult = flightPaths['/api/flights/single-cheapest-search']['get']['responses'][200]['content']['application/json']

export function getOfferFlightPrice(params: FlightPriceParams) {
  const query = qs.stringify({
    start_date: moment().add(2, 'day').format(ISO_DATE_FORMAT),
    end_date: params.travelToDate ? params.travelToDate : moment().add(1, 'year').format(ISO_DATE_FORMAT),
    number_of_nights: params.duration,
    number_of_adults: 2,
    origin: params.flightOrigin,
    destination: params.flightDestination,
    currency: params.currency,
    region: params.region,
    force_bundle_id: params.forceBundleId,
  })

  return request.get<FlightPriceResult>(`/api/flights/single-cheapest-search?${query}`).then(response => {
    return response.result?.journey?.cost ?? 0
  })
}

export function streamRequest(params: SearchRequestPayload): void {
  const {
    filters = {},
    region,
    canViewLuxPlusBenefits,
    isLuxPlusEnabled,
    noCache,
    memberId,
    domainUserId,
    email,
    searchId,
    minRequiredOffers,
    personalise,
    include,
    publicRates,
    typeaheadId,
    functionScoreWeights,
    lang,
  } = params

  let searchType: string | undefined = undefined
  let lat: number | undefined = undefined
  let lng: number | undefined = undefined

  const destinationIds = getDestinationIds(filters)
  const landmarkIds = getLandmarkIds(filters)

  if (destinationIds && destinationIds.length > 0) {
    searchType = 'destination'
  } else if (landmarkIds && landmarkIds.length > 0) {
    searchType = 'landmark'
  }

  if (filters.lat && filters.lng) {
    searchType = 'lat_long'
    lat = filters.lat
    lng = filters.lng
  }
  if (filters.bounds) {
    searchType = 'map_area'
  }

  const scheduleVisibilities = filters.scheduleVisibilities ? [...filters.scheduleVisibilities] : []

  if (!scheduleVisibilities.includes('listVisible')) {
    scheduleVisibilities.push('listVisible')
  }

  if (isLuxPlusEnabled && !scheduleVisibilities.includes('luxPlusEarlyAccessVisible')) {
    scheduleVisibilities.push('luxPlusEarlyAccessVisible')
  }

  if (canViewLuxPlusBenefits && !scheduleVisibilities.includes('luxPlusExclusiveVisible')) {
    scheduleVisibilities.push('luxPlusExclusiveVisible')
  }

  const filteredOfferTypes = getOfferTypesFromFilters(filters) as Array<App.OfferListOfferType>
  // Preserve undefined (not []) when all offer types were stripped so isHotelSpecificRequest
  // does not treat an empty array as hotel-only ([].every(...) is true).
  const searchOfferTypes = filters.offerTypes?.length && !filteredOfferTypes.length ? undefined : filteredOfferTypes
  const searchFilters = {
    ...filters,
    offerTypes: filters.offerTypes === undefined ? undefined : searchOfferTypes,
  }
  const types = searchFilters.offerTypes ?? []

  const commonParams = {
    offerType: types.join(',') || undefined,
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
    flexibleMonths: filters.flexibleMonths || undefined,
    flexibleNights: filters.flexibleNights || undefined,
    campaigns: filters.campaigns?.join(',') || undefined,
    holidayTypes: filters.holidayTypes?.join(',') || undefined,
    holidayTypesScoped: filters.holidayTypesScoped?.join(',') || undefined,
    propertyTypes: filters.propertyTypes?.join(',') || undefined,
    locations: filters.locations?.join(',') || undefined,
    searchType: searchType as SearchInitialRequest['searchType'],
    placeIds: getPlaceIdsForSearchApi(filters),
    lat: lat,
    lng: lng,
    query: filters.query,
    distanceEq: filters.distanceEq,
    distanceGt: filters.distanceGt,
    sortBy: filters.sortBy as SearchInitialRequest['sortBy'],
    strategyApplied: filters.strategyApplied,
    region: region as SearchInitialRequest['region'],
    occupancy: filters.rooms ? serialiseOccupancy(filters.rooms) : undefined,
    propertyId: filters.propertyId,
    searchNearby: filters.searchNearby,
    bounds: filters.bounds,
    amenities: filters.amenities?.join(',') || undefined,
    propertyFacilities: filters.propertyFacilities?.join(',') || undefined,
    roomAmenities: filters.roomAmenities?.join(',') || undefined,
    bedTypes: filters.bedTypes?.join(',') || undefined,
    limit: filters.limit || undefined,
    propertySubCategories: filters.propertySubCategories?.join(',') || undefined,
    starRatingGte: filters.starRatingGte,
    customerRatingGte: filters.customerRatingGte,
    inclusions: filters.inclusions?.join(',') || undefined,
    inclusionGap: filters.inclusionGap || undefined,
    tags: filters.tags?.length ? filters.tags : undefined,
    offerTags: filters.offerTags?.join(',') || undefined,
    priceLte: filters.priceLte ? safeNumberConversion(filters.priceLte) : undefined,
    priceGte: filters.priceGte ? safeNumberConversion(filters.priceGte) : undefined,
    pricingType: filters.pricingType,
    pointsLte: filters.pointsLte,
    pointsGte: filters.pointsGte,
    productTypes: filters.productTypes,
    bedroomsGte: filters.bedroomsGte && filters.bedroomsGte > 0 ? filters.bedroomsGte : undefined,
    bedroomsEq: filters.bedroomsEq && filters.bedroomsEq > 0 ? filters.bedroomsEq : undefined,
    bedsGte: filters.bedsGte && filters.bedsGte > 0 ? filters.bedsGte : undefined,
    bedsEq: filters.bedsEq && filters.bedsEq > 0 ? filters.bedsEq : undefined,
    bathroomsGte: filters.bathroomsGte && filters.bathroomsGte > 0 ? filters.bathroomsGte : undefined,
    bathroomsEq: filters.bathroomsEq && filters.bathroomsEq > 0 ? filters.bathroomsEq : undefined,
    scheduleVisibilities: scheduleVisibilities?.join(',') || undefined,
    luxPlusExclusive: filters.luxPlusFeatures && filters.luxPlusFeatures.length > 0 ? filters.luxPlusFeatures?.join(',') : undefined,
    agentHubExclusive: filters.agentHubFeatures && filters.agentHubFeatures.length > 0 ? filters.agentHubFeatures?.join(',') : undefined,
    includeAllBedbankWithSales: filters.includeAllBedbankWithSales || undefined,
    isLuxPlusMember: filters.isLuxPlusMember ?? (canViewLuxPlusBenefits || undefined),
    leUserId: memberId,
    domainUserId,
    personalise,
    include,
    publicRates,
    rollout: filters.rollout,
    fareClass: filters.fareClass,
    originAirportCode: filters.feederOriginAirportCode,
    charterNumberOfNights: filters.charterNumberOfNights,
    societe: filters.loyalty?.join(',') || undefined,
    paymentOptions: filters.paymentOptions?.join(',') || undefined,
    chains: filters.chains?.length ? filters.chains : undefined,
    subChains: filters.subChains?.length ? filters.subChains : undefined,
  }
  if (isHotelSpecificRequest({ filters: searchFilters })) {
    const searchRequest: SearchInitialRequest = {
      ...commonParams,
      ...(commonParams.placeIds?.length ? { placeIds: commonParams.placeIds } : { placeIds: ANYWHERE_PLACE_ID }),
      ...(filters.segments?.length ? { segments: filters.segments.join(',') } : {}),
      ...(params.clientCheckAvailability && { clientCheckAvailability: true }),
      ...(noCache && { noCache: true }),
      ...(functionScoreWeights ? { functionScoreWeights } : {}),
      ...(minRequiredOffers ? { minOffersRequired: minRequiredOffers } : {}),
      ...(searchId ? { searchId } : {}),
      ...(typeaheadId ? { typeaheadId } : {}),
      ...(lang ? { lang } : {}),
      occupancy: commonParams.occupancy || [],
      brand: config.BRAND as SearchInitialRequest['brand'],
      ...(email ? { email } : {}),
      ...(filters.excludeCharterOffers ? { excludeCharterOffers: true } : {}),
      ...(filters.lereGroup ? { lereGroup: filters.lereGroup } : {}),
      ...(filters.includeOutOfBudgetOffers ? { includeOutOfBudgetOffers: true } : {}),
    }

    if (filters.rooms && !isValidOccupancy(...filters.rooms)) {
      return
    }

    emitSocketMessage(
      searchId!,
      'search:request:initial',
      searchRequest,
    )
  }
}

type ServerPropertyMapping = accommodationDefs['PropertyMapping']

export function getPropertyOfferMapping(
  offerType: typeof OFFER_TYPE_ALWAYS_ON | typeof OFFER_TYPE_BED_BANK,
  offerId: string,
): Promise<App.PropertyOfferMapping> {
  const query = qs.stringify({
    ...(offerType === OFFER_TYPE_ALWAYS_ON && { offerId }),
    ...(offerType === OFFER_TYPE_BED_BANK && { bedbankPropertyId: offerId }),
    allowMysteryRooms: true,
  })

  return request.get<App.ApiResponse<ServerPropertyMapping>>(
    `/api/accommodation/properties/mappings?${query}`,
    { excludeBrand: true },
  )
    .then(response => {
      const serverMapping = response.result
      const allRoomMaps = Object.values(serverMapping.rooms)
      return {
        propertyId: serverMapping.id,
        lpcOfferIds: serverMapping.offerIds ?? [],
        lpcPropertyIds: serverMapping.reservationIds ?? [],
        bedbankPropertyIds: serverMapping.bedbankIds ?? [],
        isUltraLux: serverMapping.isUltraLux ?? false,
        hasCampaigns: serverMapping.hasActiveCampaigns ?? false,
        hasBundles: serverMapping.hasActiveBundles ?? false,
        // TODO(HP-2326): Remove fallbacks once mapping endpoint reliably returns `hasPromotions`
        // hint.
        //
        // NB: We can't tell if there are any promotions synthesised from active bundles because
        // `serverMapping.activeBundles` doesn't indicate whether the bundle has a merchandising
        // label configured.
        hasPromotions: (serverMapping.hasActiveCampaigns || serverMapping.hasPromotions) ?? false,
        rooms: arrayToObject(
          allRoomMaps,
          room => room.id,
          room => ({
            roomId: room.id,
            lpcRoomIds: room.reservationIds?.length ? room.reservationIds : EmptyArray,
            bedbankRoomIds: room.bedbankIds?.length ? room.bedbankIds : EmptyArray,
          }),
        ),
      }
    })
}

export function getOfferIdBySlug(
  slug: string,
): Promise<string | undefined> {
  if (detectOfferTypeFromId(slug)) {
    // isn't a slug, it's an actual offer id resolve straight away
    return Promise.resolve(undefined)
  }

  const uri = templates.offer.publicOfferRetrieveId.expand({ slug, type: 'offer' })

  return request.get<App.ApiResponse<PublicOfferV2.RetrieveIdResponseBody['result']>>(uri)
    .then(async response => response.result.offerId)
}

export async function downloadPdfFromSignedUrl(signedUrl: string, filename: string): Promise<void> {
  const link = document.createElement('a')
  link.href = signedUrl
  link.download = filename
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()

  setTimeout(() => {
    document.body.removeChild(link)
  }, 100)
}

interface GetPackageRecommendationsParams {
  offerId: string
  region: string
  occupancy?: string
  checkIn?: string
  checkOut?: string
}

export function getPackageRecommendations(params: GetPackageRecommendationsParams): Promise<PackageRecommendationsResponse> {
  const query = qs.stringify({
    region: params.region,
    occupancy: params.occupancy,
    checkIn: params.checkIn,
    checkOut: params.checkOut,
  })

  return request.get<PackageRecommendationsResponse>(
    `/api/v2/public-offers/${params.offerId}/package-recommendations?${query}`,
  )
}

export async function generatePresignedUrl(params: PresignedUrlRequest): Promise<PresignedUrlResponse> {
  const response = await request.post<PresignedUrlResponse, PresignedUrlRequest>(
    '/api/offers/presigned-url',
    params,
    { credentials: 'include' },
  )

  return response
}
